import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const copperForge: TemplateRegistration = {
  meta: {
    id: 'copper-forge',
    name: 'Copper Forge',
    category: 'Modern',
    description: 'Industrial copper accents with magazine-style columns',
    previewImage: 'https://images.unsplash.com/photo-1530124566582-a45a7c40e37b?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Bebas Neue', 'Karla']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="container">
          <div class="magazine-layout">
            <div class="mag-col-wide">
              <div class="copper-line"></div>
              <h2 class="section-title">About Us</h2>
              <p class="about-text">${safeAbout}</p>
            </div>
            <div class="mag-col-narrow">
              <div class="copper-accent-bar"></div>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const isWide = i % 3 === 0;
        return `
          <div class="service-card ${isWide ? 'service-wide' : ''}">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What We Do</h2>
          <div class="industrial-divider"></div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="industrial-divider-sm"></div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #B87333; --bg: #2D2D2D; --bg-alt: #1F1F1F; --accent: #B87333;
      --text: #E8E8E8; --text-muted: #999999;
      --font-heading: 'Bebas Neue', sans-serif; --font-body: 'Karla', sans-serif;
      --copper-gradient: linear-gradient(135deg, #B87333, #D4955A, #B87333);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(31,31,31,.95); backdrop-filter: blur(8px); border-bottom: 2px solid; border-image: var(--copper-gradient) 1; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.8rem; color: var(--text); letter-spacing: 4px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 2px; font-weight: 500; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 28px; height: 2px; background: var(--accent); margin: 6px 0; transition: .3s; }

    /* INDUSTRIAL DIVIDERS */
    .industrial-divider { width: 100%; height: 2px; background: var(--copper-gradient); margin-bottom: 40px; }
    .industrial-divider-sm { width: 60px; height: 2px; background: var(--copper-gradient); margin: 16px 0; }
    .copper-line { width: 80px; height: 3px; background: var(--copper-gradient); margin-bottom: 20px; }
    .copper-accent-bar { width: 4px; height: 100%; min-height: 160px; background: var(--copper-gradient); border-radius: 2px; }

    /* HERO - split-screen layout */
    .hero { position: relative; min-height: 100vh; display: flex; overflow: hidden; }
    .hero-split { display: flex; width: 100%; min-height: 100vh; }
    .hero-left { flex: 1; background: var(--bg-alt); display: flex; flex-direction: column; justify-content: center; padding: 120px 48px 80px; position: relative; z-index: 2; }
    .hero-right { flex: 1; position: relative; overflow: hidden; }
    .hero-right img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-accent-line { position: absolute; top: 0; bottom: 0; left: 50%; width: 4px; z-index: 3; background: var(--copper-gradient); transform: skewX(-4deg); }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(3.5rem, 9vw, 7rem); line-height: 1; letter-spacing: 6px; margin-bottom: 16px; }
    .hero h1 .copper-text { background: var(--copper-gradient); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 520px; margin-bottom: 32px; letter-spacing: .5px; }
    .hero-cta { display: inline-block; padding: 16px 44px; background: transparent; color: var(--accent); font-family: var(--font-heading); font-size: 1.1rem; letter-spacing: 4px; border: 2px solid; border-image: var(--copper-gradient) 1; cursor: pointer; transition: background .3s, color .3s; }
    .hero-cta:hover { background: var(--accent); color: var(--bg); opacity: 1; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3.2rem); letter-spacing: 4px; margin-bottom: 48px; }

    /* MAGAZINE LAYOUT */
    .magazine-layout { display: grid; grid-template-columns: 2fr 1fr; gap: 48px; align-items: start; }
    .mag-col-wide { padding-right: 20px; }
    .mag-col-narrow { display: flex; justify-content: center; padding-top: 20px; }

    /* ABOUT */
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 800px; line-height: 1.9; column-count: 2; column-gap: 36px; }

    /* SERVICES */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .service-card { background: var(--bg); border: 1px solid #3a3a3a; padding: 36px 28px; position: relative; transition: border-color .3s; }
    .service-card::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: var(--copper-gradient); transform: scaleX(0); transition: transform .3s; transform-origin: left; }
    .service-card:hover { border-color: var(--accent); }
    .service-card:hover::after { transform: scaleX(1); }
    .service-wide { grid-column: span 2; }
    .service-icon { color: var(--accent); margin-bottom: 14px; }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.4rem; letter-spacing: 2px; margin-bottom: 10px; }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 14px; color: var(--accent); font-weight: 700; font-size: 1.1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; filter: sepia(.15); transition: filter .3s, transform .3s; }
    .gallery-item img:hover { filter: sepia(0); transform: scale(1.02); }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 32px; }
    .testimonial-card { padding: 32px; background: var(--bg); border: 1px solid #3a3a3a; }
    .testimonial-body p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 4px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; font-family: var(--font-heading); letter-spacing: 1px; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #555; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 1px solid #3a3a3a; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.3rem; letter-spacing: 2px; margin-bottom: 12px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid #3a3a3a; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.2rem; letter-spacing: 2px; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.6rem; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #3a3a3a; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #3a3a3a; padding: 20px 0; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--copper-gradient); color: var(--bg); font-family: var(--font-heading); padding: 8px 24px; font-size: 1.1rem; letter-spacing: 2px; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 2px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #3a3a3a; border: 1px solid #555; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: transparent; color: var(--accent); font-family: var(--font-heading); font-size: 1.1rem; letter-spacing: 4px; border: 2px solid; border-image: var(--copper-gradient) 1; cursor: pointer; transition: background .3s, color .3s; }
    .contact-form button:hover { background: var(--accent); color: var(--bg); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 2px solid; border-image: var(--copper-gradient) 1; padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; letter-spacing: 1px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(31,31,31,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .magazine-layout { grid-template-columns: 1fr; }
      .mag-col-narrow { display: none; }
      .about-text { column-count: 1; }
      .services-grid { grid-template-columns: 1fr; }
      .service-wide { grid-column: span 1; }
      .hero-split { flex-direction: column-reverse; }
      .hero-left { padding: 60px 24px 60px; }
      .hero-right { flex: none; height: 50vh; }
      .hero-accent-line { top: auto; bottom: 50vh; left: 0; right: 0; width: 100%; height: 4px; transform: skewY(-2deg); }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-left { padding: 80px 16px 40px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-split">
      <div class="hero-left">
        <h1><span class="copper-text">${safeHeadline}</span></h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
      <div class="hero-right">
        ${heroImage ? `<img src="${escapeAttr(heroImage)}" alt="${safeName}">` : ''}
      </div>
    </div>
    <div class="hero-accent-line"></div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
